import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { disableBlueTapHighlight } from 'shop/components/common'
import { StyledHTMLElement } from 'shop/theme/types'
import { TIME_UNITS } from 'shop/utils/date'

type Props = {
  variant: 'date' | 'time'
  selectedFlexibilitySeconds: number | null
  setSelectedFlexibilitySeconds: React.Dispatch<
    React.SetStateAction<number | null>
  >
}

type FlexibilityOption = {
  label: string
  timeInSeconds: number
}

// divide by 1000 to get second values to match flexibilitySeconds
const MINUTE = TIME_UNITS.MINUTE / 1000
const HOUR = TIME_UNITS.HOUR / 1000
const DAY = TIME_UNITS.DAY / 1000

/** Map second values to readable string of time */
export const getFlexibilityValue = (flexibilitySeconds: number): string => {
  const formatUnit = (value: number, unit: string) =>
    `${value} ${unit}${value === 1 ? '' : 's'}`

  if (flexibilitySeconds < MINUTE)
    return formatUnit(flexibilitySeconds, 'second')

  if (flexibilitySeconds < HOUR)
    return formatUnit(Math.floor(flexibilitySeconds / MINUTE), 'minute')

  if (flexibilitySeconds < DAY)
    return formatUnit(Math.floor(flexibilitySeconds / HOUR), 'hour')

  return formatUnit(Math.floor(flexibilitySeconds / DAY), 'day')
}

const DateTimeFlexibilityButtons = ({
  variant,
  selectedFlexibilitySeconds,
  setSelectedFlexibilitySeconds
}: Props) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)

  const dateOptions: FlexibilityOption[] = [
    {
      label: getFlexibilityValue(24 * HOUR),
      timeInSeconds: 1 * DAY
    }
  ]
  const timeOptions: FlexibilityOption[] = [
    {
      label: getFlexibilityValue(30 * MINUTE),
      timeInSeconds: 30 * MINUTE
    },
    {
      label: getFlexibilityValue(1 * HOUR),
      timeInSeconds: 1 * HOUR
    },
    {
      label: getFlexibilityValue(2 * HOUR),
      timeInSeconds: 2 * HOUR
    }
  ]

  const renderOptions = variant === 'date' ? dateOptions : timeOptions

  useEffect(() => {
    // clear any selected options when we change between variants
    if (selectedOption) {
      handleSelect(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant])

  useEffect(() => {
    // default to previously selected option on component load
    if (selectedFlexibilitySeconds && !selectedOption) {
      setSelectedOption(getFlexibilityValue(selectedFlexibilitySeconds))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlexibilitySeconds])

  const clearSelection = () => {
    setSelectedOption(null)
    setSelectedFlexibilitySeconds(null)
  }

  const handleSelect = (option: FlexibilityOption | null) => {
    if (option) {
      if (option.label === selectedOption) {
        clearSelection()
        return
      }
      setSelectedOption(option.label)
      setSelectedFlexibilitySeconds(option.timeInSeconds)
      return
    }
    clearSelection()
  }

  return (
    <Container>
      {renderOptions.map((option) => {
        return (
          <OptionSelector
            key={option.label}
            onClick={() => handleSelect(option)}
            isSelected={option.label === selectedOption}
            aria-pressed={option.label === selectedOption}
            role='button'
            type='button'
          >
            &plusmn; {option.label}
          </OptionSelector>
        )
      })}
    </Container>
  )
}

const Container = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
  gap: '12px'
}))

const OptionSelector = styled.button<
  StyledHTMLElement & { isSelected: boolean }
>(({ theme, isSelected }) => ({
  display: 'flex',
  width: '33%',
  justifyContent: 'center',
  border: '1px solid',
  borderColor: theme.colors.primary,
  borderRadius: theme.borderRadius,
  cursor: 'pointer',
  padding: '12px 0',
  fontWeight: 600,
  fontSize: '14px',
  color: isSelected ? '#FFF' : theme.colors.primary,
  background: isSelected ? theme.colors['primary'] : '#fff',

  ...disableBlueTapHighlight(),

  '&:hover': {
    opacity: 0.8
  },
  '&:active': {
    opacity: 0.6
  }
}))

export default DateTimeFlexibilityButtons
